import PropTypes from 'prop-types';
import React from 'react';
import Style from './inventoriesPicker.module.css';
import { useTranslation } from 'react-i18next';
import { Grid } from 'semantic-ui-react';
import { Table, TableBody, TableHeader, TableRow, TableHeaderCell, TableCell, SelectHierarchy, Status } from '@weezevent/nacre';
import { StatusEvent } from '../rates/eventsPicker';

function LinkedInventoryRow({ inventory }) {
    const { t } = useTranslation();

    return (
        <TableRow key={inventory.pk}>
            <TableCell>{inventory.name}</TableCell>
            <TableCell>{inventory.event}</TableCell>
            <TableCell>
                {inventory.end_date !== null
                    ? new Date(inventory.end_date).toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: inventory.event.timezone })
                    : '-'}{' '}
            </TableCell>
            <TableCell style={{ textAlign: 'center' }} className={'events-picker-status'}>
                <Status
                    text={inventory.active ? t('ticket.charts.newInventory.inventory_status_active') : t('ticket.charts.newInventory.inventory_status_inactive')}
                    color={inventory.active ? 'green' : 'orange'}
                />
            </TableCell>
            <TableCell style={{ textAlign: 'center' }}>-</TableCell>
        </TableRow>
    );
}

LinkedInventoryRow.propTypes = {
    inventory: PropTypes.object.isRequired
};

export default function InventoriesPicker({ activeInventories = [], currentSelected = [], onSave = () => {} }) {
    const { t } = useTranslation();

    const [selectedInventories, setSelectedInventories] = React.useState([]);

    const selectInventory = React.useCallback(
        inventoryId => {
            let newSelectedInventories = [activeInventories.find(inventory => inventoryId === inventory.pk), ...selectedInventories];
            setSelectedInventories(newSelectedInventories);
            onSave(newSelectedInventories);
        },
        [selectedInventories, activeInventories]
    );

    const deselectInventory = React.useCallback(
        inventory => {
            let newSelectedInventories = selectedInventories.filter(selectedInventory => inventory.pk !== selectedInventory.pk);
            setSelectedInventories(newSelectedInventories);
            onSave(newSelectedInventories);
        },
        [selectedInventories]
    );

    React.useEffect(() => {
        if (currentSelected.length > 0) {
            setSelectedInventories(currentSelected, ...selectedInventories);
        }
    }, [currentSelected]);

    function stylizeEventOption(inventory) {
        const { event, name, virtual } = inventory;
        const { start_date, timezone } = event;

        const formattedDate = new Date(start_date).toLocaleDateString([], {
            minute: '2-digit',
            hour: '2-digit',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: timezone
        });

        return (
            <div className="events-picker-options">
                <div>
                    <div className="events-picker-options-name">{event.name}</div>
                    <div className="events-picker-options-date">
                        {formattedDate} • {`${name} ${virtual ? t('ticket.charts.inventory.group') : ''}`}
                    </div>
                </div>
                <div>
                    <StatusEvent status={inventory.event.publication_status} />
                </div>
            </div>
        );
    }

    const inventoriesOptions = React.useMemo(() => {
        const order = { true: 1, false: 2 };
        if (!activeInventories) return [];
        return activeInventories
            .filter(inventory => !selectedInventories.find(inventory_ => inventory_.pk === inventory.pk))
            .sort((el1, el2) => order[el1.virtual] - order[el2.virtual] || new Date(el1.event.start_date).getTime() - new Date(el2.event.start_date).getTime())
            .map(inventory => {
                return {
                    key: inventory.uuid,
                    text: stylizeEventOption(inventory),
                    name: inventory.name,
                    value: inventory.uuid
                };
            });
    }, [activeInventories, selectedInventories]);

    const [querySearch, setQuerySearch] = React.useState('');

    const handleQuery = React.useCallback(value => {
        setQuerySearch(value);
    });

    const filteredInventories = React.useMemo(() => {
        const searchValue = querySearch?.trim().toLowerCase() ?? '';

        if (!searchValue) {
            return inventoriesOptions;
        }

        return inventoriesOptions?.filter(ev => ev.name.toLowerCase().includes(searchValue));
    }, [inventoriesOptions, querySearch]);

    return (
        <div>
            <Grid>
                <Grid.Column mobile={12} tablet={9} computer={9}>
                    <div className={Style['selector-container']}>
                        <SelectHierarchy
                            closeDropdownOnSelect={false}
                            widthIndent="10px"
                            options={inventoriesOptions}
                            externalOptions={filteredInventories}
                            placeholder={t('ticket.charts.newInventory.inventories_picker_dropdown_placeholder')}
                            onChange={(synth, data) => {
                                selectInventory(data.key);
                            }}
                            onExternalSearch={handleQuery}
                            searchValue={querySearch}
                        />
                    </div>
                </Grid.Column>
                <Grid.Column mobile={4} tablet={7} computer={7}>
                    <div className={'inventories-picker-count-events'}>
                        {selectedInventories.length > 0 && t('ticket.charts.newInventory.batchBooking_count', { count: selectedInventories.length })}
                    </div>
                </Grid.Column>
            </Grid>
            <div className="modal-container" />
            {selectedInventories.length > 0 && (
                <Table basic celled>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell>{t('ticket.charts.newInventory.table_to_link_inventories_inventory_name')}</TableHeaderCell>
                            <TableHeaderCell>{t('ticket.charts.newInventory.table_to_link_inventories_event_linked')}</TableHeaderCell>
                            <TableHeaderCell>{t('ticket.charts.newInventory.table_to_link_inventories_inventory_end_date')}</TableHeaderCell>
                            <TableHeaderCell style={{ textAlign: 'center' }}>{t('ticket.charts.newInventory.table_to_link_inventories_inventory_status')}</TableHeaderCell>
                            <TableHeaderCell style={{ width: '100px' }}></TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {selectedInventories.map(inventory => (
                            <TableRow key={inventory.pk}>
                                <TableCell>{inventory.name}</TableCell>
                                <TableCell>{inventory.event.name}</TableCell>
                                <TableCell>
                                    {inventory.end_date !== null
                                        ? new Date(inventory.end_date).toLocaleDateString([], {
                                              day: '2-digit',
                                              month: '2-digit',
                                              year: 'numeric',
                                              timeZone: inventory.event.timezone
                                          })
                                        : '-'}
                                </TableCell>
                                <TableCell style={{ textAlign: 'center' }} className={'events-picker-status'}>
                                    <Status
                                        text={
                                            inventory.active ? t('ticket.charts.newInventory.inventory_status_active') : t('ticket.charts.newInventory.inventory_status_inactive')
                                        }
                                        color={inventory.active ? 'green' : 'orange'}
                                    />
                                </TableCell>
                                <TableCell style={{ textAlign: 'center' }}>
                                    {inventory.active && (
                                        <button className="form-remove-event" onClick={() => deselectInventory(inventory)}>
                                            {t('ticket.common.action.remove')}
                                        </button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </div>
    );
}

InventoriesPicker.propTypes = {
    onSave: PropTypes.func.isRequired,
    activeInventories: PropTypes.array.isRequired,
    currentSelected: PropTypes.array.isRequired
};
